// Per-page SEO head injection: canonical, Open Graph, Twitter Cards, JSON-LD.
// Uses the shared settings from SettingsContext for org-level defaults.
import { useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";

const upsertMeta = (attr, value, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const removeMeta = (attr, value) => {
  const el = document.querySelector(`meta[${attr}="${value}"]`);
  if (el) el.remove();
};

const upsertCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
};

const upsertJsonLd = (id, obj) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
};

/**
 * useSEO({ title, description, image, type, jsonLd, breadcrumbs, noIndex })
 * All fields optional. Falls back to Business Settings + sensible defaults.
 * type: "website" | "article" | "product"
 * breadcrumbs: [{ name, url }]
 * jsonLd: object or array of objects — additional structured data (Product, Article, etc.)
 */
export function useSEO(opts = {}) {
  const { settings } = useSettings();

  useEffect(() => {
    if (!settings) return;
    const brand = settings.business_name || "BOLAWPRESS";
    const baseTitle = settings.seo_title || brand;
    const finalTitle = opts.title ? `${opts.title} · ${brand}` : baseTitle;
    const finalDesc = opts.description || settings.seo_description || `${brand} — premium commercial printing, graphic design, and web experience.`;
    const url = window.location.origin + window.location.pathname;
    const image = opts.image || settings.logo_url || "";
    const type = opts.type || "website";
    const twitter = settings.social_links?.twitter || settings.social_links?.x || "";
    const twitterHandle = twitter.replace(/^https?:\/\/(www\.)?(twitter|x)\.com\//, "@").replace(/\/$/, "");

    document.title = finalTitle;

    upsertMeta("name", "description", finalDesc);
    upsertCanonical(url);

    // Open Graph
    upsertMeta("property", "og:site_name", brand);
    upsertMeta("property", "og:title", finalTitle);
    upsertMeta("property", "og:description", finalDesc);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    if (image) upsertMeta("property", "og:image", image);

    // Twitter Card
    upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", finalTitle);
    upsertMeta("name", "twitter:description", finalDesc);
    if (image) upsertMeta("name", "twitter:image", image);
    if (twitterHandle) upsertMeta("name", "twitter:site", twitterHandle);

    // robots
    if (opts.noIndex) upsertMeta("name", "robots", "noindex, nofollow");
    else removeMeta("name", "robots");

    // Breadcrumbs JSON-LD
    if (opts.breadcrumbs && opts.breadcrumbs.length > 0) {
      upsertJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: opts.breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.url.startsWith("http") ? b.url : (window.location.origin + b.url),
        })),
      });
    } else {
      const el = document.getElementById("ld-breadcrumb");
      if (el) el.remove();
    }

    // Page-specific JSON-LD (Product, Article, etc.)
    if (opts.jsonLd) {
      const arr = Array.isArray(opts.jsonLd) ? opts.jsonLd : [opts.jsonLd];
      arr.forEach((obj, i) => upsertJsonLd(`ld-page-${i}`, obj));
    } else {
      // clean up previous page-specific ld scripts
      let i = 0;
      while (true) {
        const el = document.getElementById(`ld-page-${i}`);
        if (!el) break;
        el.remove();
        i++;
      }
    }

    return () => {
      // cleanup breadcrumb + page-specific on unmount
      const bc = document.getElementById("ld-breadcrumb");
      if (bc) bc.remove();
      let i = 0;
      while (true) {
        const el = document.getElementById(`ld-page-${i}`);
        if (!el) break;
        el.remove();
        i++;
      }
    };
  }, [settings, opts.title, opts.description, opts.image, opts.type, opts.noIndex, JSON.stringify(opts.jsonLd || null), JSON.stringify(opts.breadcrumbs || null)]);
}

export default useSEO;
